<template>
  <div class="signup-complete">
    <div class="card">
      <h1 class="title">無料体験セットアップ</h1>

      <!-- リンク未経由でアクセスした場合 -->
      <div v-if="sessionReady && !session" class="message-box error">
        <p>このページはメール内のリンクからアクセスしてください。まだメールを送っていない場合は<a href="/">新規登録</a>からメールアドレスを入力してください。</p>
      </div>

      <!-- Stripe キャンセル戻り -->
      <div v-else-if="isCancelReturn" class="message-box info">
        <p>お支払いがキャンセルされました。以下から再度お支払いへ進めます。</p>
      </div>

      <!-- 既に申込済み -->
      <div v-else-if="sessionReady && session && alreadySubscribed" class="message-box info">
        <p>既に申込済みです。<router-link to="/top">トップページ</router-link>からご利用ください。</p>
      </div>

      <!-- フォーム表示（セッションあり & 未申込） -->
      <form v-else-if="sessionReady && session && !alreadySubscribed" @submit.prevent="handleSubmit" class="form">
        <p class="step-desc">メールアドレスは登録済みです。以下の入力を行ってください。</p>

        <div class="form-section">
          <h4>👤 親御様</h4>
          <div class="form-group">
            <label for="parent-name">親氏名 <span class="required">必須</span></label>
            <input
              id="parent-name"
              v-model="form.parentName"
              type="text"
              required
              placeholder="山田 太郎"
              class="form-input"
            />
          </div>
        </div>

        <div class="form-section">
          <h4>👧 お子さま</h4>
          <div class="form-group">
            <label for="child-name">子氏名 <span class="required">必須</span></label>
            <input
              id="child-name"
              v-model="form.childName"
              type="text"
              required
              placeholder="山田 花子"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label for="child-kana">子氏名（ふりがな） <span class="required">必須</span></label>
            <input
              id="child-kana"
              v-model="form.childKana"
              type="text"
              required
              placeholder="やまだ はなこ"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label for="child-grade">子の学年</label>
            <select id="child-grade" v-model="form.childGrade" class="form-input">
              <option value="">選択してください</option>
              <option value="年長">年長</option>
              <option value="小1">小1</option>
              <option value="小2">小2</option>
              <option value="小3">小3</option>
              <option value="小4">小4</option>
              <option value="小5">小5</option>
              <option value="小6">小6</option>
              <option value="中1">中1</option>
              <option value="中2">中2</option>
              <option value="中3">中3</option>
              <option value="その他">その他</option>
            </select>
          </div>
        </div>

        <div class="form-section">
          <h4>🔐 パスワード</h4>
          <p class="hint">ログイン時に使うパスワードを設定してください（8文字以上推奨）。</p>
          <div class="form-group">
            <label for="password">パスワード <span class="required">必須</span></label>
            <input
              id="password"
              v-model="form.password"
              type="password"
              required
              minlength="6"
              placeholder="6文字以上"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label for="password-confirm">パスワード（確認） <span class="required">必須</span></label>
            <input
              id="password-confirm"
              v-model="form.passwordConfirm"
              type="password"
              required
              minlength="6"
              placeholder="同じパスワードを入力"
              class="form-input"
            />
          </div>
          <p v-if="form.password && form.passwordConfirm && form.password !== form.passwordConfirm" class="validation-error">
            パスワードが一致しません
          </p>
        </div>

        <div class="form-section payment-info">
          <h4>💳 サブスクの仕組み</h4>
          <ul>
            <li><strong>14日間無料体験</strong> — トライアル期間中は料金は発生しません。</li>
            <li><strong>課金開始</strong> — 無料期間終了後、月額9,800円（税込）の課金が自動で開始されます。</li>
            <li><strong>解約</strong> — 無料期間内に解約すれば料金はかかりません。解約は登録メールアドレス宛にお送りする解約用リンクから行えます（アプリ内では解約できません）。</li>
          </ul>
        </div>

        <div class="form-section agreement-section">
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="form.agreeTerms" class="checkbox-input" />
              <span class="checkbox-text">
                <a href="https://otoeigo.com/terms" target="_blank" rel="noopener noreferrer" class="link" @click.stop>利用規約</a>および
                <a href="https://otoeigo.com/privacy" target="_blank" rel="noopener noreferrer" class="link" @click.stop>プライバシーポリシー</a>に同意する
              </span>
            </label>
          </div>
        </div>

        <div v-if="submitError" class="message-box error">{{ submitError }}</div>
        <button type="submit" :disabled="isSubmitting || !form.agreeTerms" class="btn btn-primary">
          {{ isSubmitting ? '送信中...' : '入力内容を確認して支払いへ進む' }}
        </button>
      </form>

      <!-- セッション確認中 -->
      <div v-else class="message-box">
        <p>読み込み中...</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { supabase } from '../supabaseClient'
import { getApiBase } from '../utils/apiBase.js'

const route = useRoute()
const session = ref(null)
const sessionReady = ref(false)
const alreadySubscribed = ref(false)
const isSubmitting = ref(false)
const submitError = ref('')

const isCancelReturn = computed(() => route.query.cancel === '1')

const form = ref({
  parentName: '',
  childName: '',
  childKana: '',
  childGrade: '',
  password: '',
  passwordConfirm: '',
  agreeTerms: false
})

// リダイレクト後のハッシュからセッションを確立（Supabase が URL ハッシュを検出してセッションを復元）
onMounted(async () => {
  try {
    // ハッシュ処理のため少し待ってからセッション取得
    await new Promise(r => setTimeout(r, 100))
    const { data: { session: s } } = await supabase.auth.getSession()
    session.value = s
    if (s?.user) {
      const { data: profile } = await supabase
        .from('users_phase2')
        .select('stripe_customer_id, subscription_status')
        .eq('id', s.user.id)
        .single()
      if (profile?.stripe_customer_id && ['active', 'trialing'].includes(profile?.subscription_status)) {
        alreadySubscribed.value = true
      }
    }
  } finally {
    sessionReady.value = true
  }
})

const handleSubmit = async () => {
  if (form.value.password !== form.value.passwordConfirm) {
    submitError.value = 'パスワードが一致しません'
    return
  }
  if (!form.value.agreeTerms) {
    submitError.value = '利用規約およびプライバシーポリシーに同意してください'
    return
  }
  if (!form.value.parentName?.trim() || !form.value.childName?.trim()) {
    submitError.value = '親氏名と子氏名は必須です'
    return
  }

  isSubmitting.value = true
  submitError.value = ''

  try {
    const { data: { session: s } } = await supabase.auth.getSession()
    if (!s?.access_token) {
      submitError.value = 'セッションが切れています。メールのリンクから再度アクセスしてください。'
      return
    }

    // パスワードを設定
    const { error: pwdError } = await supabase.auth.updateUser({ password: form.value.password })
    if (pwdError) throw pwdError

    const apiBase = getApiBase()
    const url = `${apiBase ? apiBase + '/' : ''}api/signup-complete`
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${s.access_token}`
      },
      body: JSON.stringify({
        parentName: form.value.parentName.trim(),
        childName: form.value.childName.trim(),
        childKana: form.value.childKana.trim(),
        childGrade: (form.value.childGrade || '').trim() || undefined
      })
    })

    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.error || '申込の続きに失敗しました')
    }
    if (data.url) {
      window.location.href = data.url
      return
    }
    submitError.value = '支払いページの取得に失敗しました'
  } catch (err) {
    console.error('signup-complete error:', err)
    submitError.value = err.message || '申込の続きに失敗しました'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.signup-complete {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1.5rem;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  width: 100%;
  max-width: 520px;
}

.title {
  font-size: 1.2rem;
  color: #333;
  margin-bottom: 1rem;
  font-weight: 600;
}

.step-desc {
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.form-section {
  margin-bottom: 1.25rem;
}

.form-section h4 {
  font-size: 1rem;
  color: #333;
  margin-bottom: 0.6rem;
}

.form-group {
  margin-bottom: 0.75rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #555;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.required { color: #c00; }

.form-input {
  width: 100%;
  padding: 0.6rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
}

.hint {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.validation-error {
  font-size: 0.85rem;
  color: #c00;
  margin-top: -0.25rem;
  margin-bottom: 0.5rem;
}

.payment-info ul {
  margin: 0;
  padding-left: 1.2rem;
  font-size: 0.9rem;
  color: #555;
  line-height: 1.6;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-input { margin-top: 0.2rem; }

.checkbox-text { font-size: 0.9rem; color: #555; }

/* リンク：青文字・下線で一目でリンクとわかる */
.link {
  color: #2563eb;
  text-decoration: underline;
  font-weight: 500;
}
.link:hover {
  color: #1d4ed8;
}

.btn {
  width: 100%;
  padding: 0.8rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.5rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a6fd8;
}

.message-box {
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  line-height: 1.5;
}

.message-box.error {
  background: #fee;
  color: #c00;
  border: 1px solid #fcc;
}

.message-box.info {
  background: #e8f4fd;
  color: #055;
  border: 1px solid #b8daff;
}

.message-box p { margin: 0; }
</style>
